import api from './api'
import type { AxiosResponse } from 'axios'

// Enums (correspondent exactement au backend)
export enum VideoPlatform {
  YOUTUBE = 'youtube',
  VIMEO = 'vimeo',
  DAILYMOTION = 'dailymotion',
  OTHER = 'other'
}

export enum GuideStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived'
}

// Types pour les guides (compatibles avec le backend)
export interface GuideLink {
  label: string
  href: string
}

export interface GuideAction {
  label: string
  to: string
}

export enum SectionMediaType {
  IMAGE = 'image',
  VIDEO = 'video',
  NONE = 'none'
}

export interface GuideSection {
  id: string
  title: string
  text?: string
  imageName?: string
  steps?: string[]
  links?: GuideLink[]
  actions?: GuideAction[]
  // Support vidéo
  videoUrl?: string
  videoPlatform?: VideoPlatform
  videoId?: string
  // Type de média déterminé automatiquement
  mediaType?: SectionMediaType
}

export interface Guide {
  _id: string
  key: string
  label: string
  description?: string
  version: string
  order?: number
  sections: GuideSection[]
  status: GuideStatus
  organizationId?: string
  tags?: string[]
  viewCount: number
  helpfulCount: number
  metadata?: Record<string, any>
  createdBy?: string
  updatedBy?: string
  createdAt: Date
  updatedAt: Date
}

export interface GuideFilters {
  search?: string
  key?: string
  version?: string
  status?: GuideStatus
  organizationId?: string
  tags?: string[]
  page?: number
  limit?: number
}

export interface GuidesResponse {
  data: Guide[]
  total: number
}

class GuidesService {
  /**
   * Récupérer tous les guides avec filtres
   */
  async findAll(filters: GuideFilters = {}): Promise<GuidesResponse> {
    const response: AxiosResponse<GuidesResponse> = await api.get('/guides', { params: filters })
    return response.data
  }

  /**
   * Récupérer un guide par ID
   */
  async findOne(id: string): Promise<Guide> {
    const response: AxiosResponse<Guide> = await api.get(`/guides/${id}`)
    return response.data
  }

  /**
   * Récupérer tous les guides d'une version
   */
  async findByVersion(version: string): Promise<Guide[]> {
    const response: AxiosResponse<{ success: boolean; data: Guide[]; message?: string }> = await api.get(`/guides/version/${version}`)
    // Le TransformInterceptor enveloppe la réponse dans {success: true, data: [...], message: '...'}
    return response.data.data || []
  }

  /**
   * Créer un guide
   */
  async create(guide: Partial<Guide>): Promise<Guide> {
    const response: AxiosResponse<Guide> = await api.post('/guides', guide)
    return response.data
  }

  /**
   * Mettre à jour un guide
   */
  async update(id: string, guide: Partial<Guide>): Promise<Guide> {
    const response: AxiosResponse<Guide> = await api.patch(`/guides/${id}`, guide)
    return response.data
  }

  /**
   * Supprimer un guide
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/guides/${id}`)
  }

  /**
   * Marquer un guide comme utile
   */
  async markAsHelpful(id: string): Promise<Guide> {
    const response: AxiosResponse<Guide> = await api.post(`/guides/${id}/helpful`)
    return response.data
  }

  /**
   * Uploader une image pour une section d'un guide (met à jour directement le guide)
   */
  async uploadImageForSection(
    guideId: string,
    sectionId: string,
    version: string,
    file: File
  ): Promise<{ url: string; filename: string }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('version', version)
    
    const response: AxiosResponse<{ success: boolean; data: { url: string; filename: string } }> = await api.post(
      `/guides/${guideId}/sections/${sectionId}/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data.data
  }

  /**
   * Obtenir l'URL d'une image de guide
   */
  getImageUrl(version: string, filename: string): string {
    return `${api.defaults.baseURL}/guides/images/${version}/${filename}`
  }
}

export const guidesService = new GuidesService()
export default guidesService

